import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import { setSvgIconSize } from 'components/Luxkit/SvgIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import React, { MouseEventHandler, ReactElement, ReactNode, useCallback } from 'react'
import styled from 'styled-components'

const Container = styled.div`
  padding: ${rem(8)};
  display: inline-flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: ${rem(8)};
  max-width: max-content;
  border-radius: ${props => props.theme.borderRadius.S};
  background: var(--background);
  color: var(--contrast);

  &.tier-bronze {
    --background: ${props => props.theme.palette.product.loyalty.bronze.gradientSecondaryBackground};
    --contrast: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
    --icon-background: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
    --icon-contrast: ${props => props.theme.palette.product.loyalty.bronze.darkContrast};
    --alt-contrast: ${props => props.theme.palette.product.loyalty.bronze.lightContrast};
  }
  &.tier-silver {
    --background: ${props => props.theme.palette.product.loyalty.silver.gradientSecondaryBackground};
    --contrast: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
    --icon-background: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
    --icon-contrast: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
    --alt-contrast: ${props => props.theme.palette.product.loyalty.silver.lightContrast};
  }
  &.tier-gold {
    --background: ${props => props.theme.palette.product.loyalty.gold.gradientSecondaryBackground};
    --contrast: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
    --icon-background: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
    --icon-contrast: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
    --alt-contrast: ${props => props.theme.palette.product.loyalty.gold.lightContrast};
  }
  &.tier-platinum {
    --background: ${props => props.theme.palette.product.loyalty.platinum.gradientSecondaryBackground};
    --contrast: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
    --icon-background: ${props => props.theme.palette.product.loyalty.platinum.normalBackground};
    --icon-contrast: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
    --alt-contrast: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const IconContainer = styled.div`
  background-color: var(--icon-background);
  display: grid;
  place-items: center;
  height: ${rem(20)};
  width: ${rem(20)};
  border-radius: ${props => props.theme.borderRadius.round};
  color: var(--icon-contrast);
  ${setSvgIconSize(16)}

  &:empty { display: none; }
`

const Title = styled(BodyText)`
  color: var(--contrast);
`

const Subtitle = styled(BodyText)`
  color: var(--alt-contrast);
  flex-shrink: 1;
`

const ClickableContainer = styled(Clickable)`
  display: grid;
  place-items: center;
`

interface Props {
  tier: App.LoyaltyTier
  icon?: ReactElement
  children: string | ReactElement | Iterable<ReactNode>
  onClick?: MouseEventHandler<HTMLButtonElement>
}

function LoyaltyTierBenefitLabel({
  tier,
  icon,
  children,
  onClick,
}: Props) {
  const handleOnClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()

    onClick?.(event)
  }, [onClick])

  return <Container className={`tier-${tier}`}>
    {!!icon && <IconContainer>{icon}</IconContainer>}
    <Title variant="medium" weight="bold">{children}</Title>
    <Subtitle variant="small" weight="bold">{getLoyaltyTierDisplayName(tier)} Benefit</Subtitle>
    {!!onClick && <ClickableContainer onClick={handleOnClick}>
      <LineInfoCircleIcon size="XS" />
    </ClickableContainer>}
  </Container>
}

export default LoyaltyTierBenefitLabel
